import { useState, useEffect, useMemo, useRef } from 'react';
import { User } from 'firebase/auth';
import { useAuth } from './useAuth';
import { AppUser } from '../types/user';
import { FamilyProfile } from '../types/familyProfile';
import { getPageCache, setPageCache, subscribePageCache } from '../utils/pageCache';

export interface UserProfile {
  uid?: string;
  email?: string;
  displayName?: string;
  photoURL?: string;
  isAdmin?: boolean;
  profilePublic?: boolean;
  completedOnboarding?: boolean;
  familyProfile?: FamilyProfile;
  completedTours?: {
    mealPlanDraft?: boolean;
    mealPlanActivated?: boolean;
    firstSwapPrompt?: boolean;
  };
  seenAnnouncements?: Record<string, boolean>;
  createdAt?: string;
  tasteTunerDismissed?: boolean;
  lastPlanCreatedAt?: string;
  contactEmail?: string;
  pendingContactEmail?: string;
  contactEmailNudgeDismissed?: boolean;
  _adminOverride?: {
    originalAdminUid: string;
    originalAdminEmail?: string;
  };
  // Add other backend fields as needed
}

// The profile cache is keyed by the Firebase uid (not the profile's uid, which
// differs under admin impersonation) so every mount of this hook for the same
// signed-in user reads the same entry.
const profileCacheKey = (uid: string | undefined) => (uid ? `user-profile:${uid}` : null);

/**
 * Fold a just-saved change into the cached profile.
 *
 * Used by surfaces that mutate profile fields directly (the name and photo
 * editor), so every mounted useAppUser — the header avatar included — shows
 * the new value immediately instead of the stale one it seeded with. A no-op
 * before the first profile fetch resolves: there's nothing to patch, and the
 * fetch will bring the saved value anyway.
 */
export const patchCachedUserProfile = (
  uid: string | undefined,
  patch: Partial<UserProfile>
): void => {
  const key = profileCacheKey(uid);
  const current = getPageCache<UserProfile>(key);
  if (!current) return;
  setPageCache(key, { ...current, ...patch });
};

// Several instances of this hook mount together on every navigation (pages,
// header, nested components), and each wants a fresh profile. Share one
// in-flight request between them so a navigation costs one profile fetch,
// not one per mounted hook. Resolves null on failure.
let inFlightProfile: { uid: string; promise: Promise<UserProfile | null> } | null = null;

const fetchProfileShared = (firebaseUser: User): Promise<UserProfile | null> => {
  if (inFlightProfile?.uid === firebaseUser.uid) {
    return inFlightProfile.promise;
  }

  const promise = (async (): Promise<UserProfile | null> => {
    try {
      const token = await firebaseUser.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/user/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include',
      });

      if (!response.ok) {
        console.error('Failed to fetch user profile');
        return null;
      }

      const data = await response.json();
      const profile: UserProfile = {
        uid: data.user?.uid,
        email: data.user?.email,
        displayName: data.user?.displayName,
        photoURL: data.user?.photoURL,
        isAdmin: data.user?.isAdmin,
        profilePublic: data.user?.profilePublic,
        completedOnboarding: data.user?.completedOnboarding,
        familyProfile: data.user?.familyProfile,
        completedTours: data.user?.completedTours,
        seenAnnouncements: data.user?.seenAnnouncements,
        createdAt: data.user?.createdAt,
        tasteTunerDismissed: data.user?.tasteTunerDismissed,
        lastPlanCreatedAt: data.user?.lastPlanCreatedAt,
        contactEmail: data.user?.contactEmail,
        pendingContactEmail: data.user?.pendingContactEmail,
        contactEmailNudgeDismissed: data.user?.contactEmailNudgeDismissed,
        _adminOverride: data.user?._adminOverride,
        // Add other fields as needed
      };
      // Identity-stable refresh: when the content hasn't changed, return the
      // cached object instead of a fresh one. The merged user's identity
      // follows this object, and consumers key mount effects on that user —
      // a fresh-but-identical profile every navigation re-fired them all
      // (each background refresh re-ran every [user]-keyed fetch effect on
      // the page). setUserProfile with the seeded object then bails in React,
      // so an unchanged refresh causes no re-render at all.
      const cached = getPageCache<UserProfile>(profileCacheKey(firebaseUser.uid));
      if (cached && JSON.stringify(cached) === JSON.stringify(profile)) {
        return cached;
      }
      setPageCache(profileCacheKey(firebaseUser.uid), profile);
      return profile;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      return null;
    } finally {
      inFlightProfile = null;
    }
  })();

  inFlightProfile = { uid: firebaseUser.uid, promise };
  return promise;
};

export const useAppUser = () => {
  const { user: firebaseUser, loading: authLoading, ...authMethods } = useAuth();
  // Seed from the last profile this session fetched, so remounts (every page
  // navigation uses this hook) get a complete merged user on the first render
  // instead of a profile-less one while the fetch below re-resolves. The
  // fetch still runs on every mount and refreshes the cache.
  const seededProfile =
    getPageCache<UserProfile>(profileCacheKey(firebaseUser?.uid)) ?? null;
  const [userProfile, setUserProfile] = useState<UserProfile | null>(seededProfile);
  const [profileLoading, setProfileLoading] = useState(false);
  // True only after a successful profile fetch. Distinct from profileLoading,
  // which starts false and can't distinguish "not started" from "done".
  const [profileLoaded, setProfileLoaded] = useState(!!seededProfile);
  // Mirrors whether a profile is currently held, so the fetch effect can skip
  // the loading flag on background refreshes without depending on the state.
  const hasProfileRef = useRef(!!seededProfile);
  useEffect(() => {
    hasProfileRef.current = !!userProfile;
  }, [userProfile]);

  // Merge Firebase user with backend profile - memoized to prevent recreation
  const appUser: AppUser | null = useMemo(() => {
    if (!firebaseUser) return null;
    
    return Object.assign(
      Object.create(Object.getPrototypeOf(firebaseUser)),
      firebaseUser,
      userProfile
    );
  }, [firebaseUser, userProfile]);

  // Another surface can write this key while we're mounted (a name or photo
  // edit calls patchCachedUserProfile); seeding alone would only pick that up
  // on the next remount.
  useEffect(() => {
    return subscribePageCache<UserProfile>(
      profileCacheKey(firebaseUser?.uid),
      (profile) => {
        setUserProfile(profile);
        setProfileLoaded(true);
      }
    );
  }, [firebaseUser?.uid]);

  useEffect(() => {
    if (!firebaseUser) {
      setUserProfile(null);
      setProfileLoaded(false);
      return;
    }

    let cancelled = false;

    const fetchUserProfile = async () => {
      // Only surface a loading state when there's nothing to show yet; with a
      // seeded profile this is a silent background refresh.
      if (!hasProfileRef.current) {
        setProfileLoading(true);
      }
      const profile = await fetchProfileShared(firebaseUser);
      if (cancelled) return;
      if (profile) {
        setUserProfile(profile);
        setProfileLoaded(true);
      }
      setProfileLoading(false);
    };

    fetchUserProfile();
    return () => {
      cancelled = true;
    };
  }, [firebaseUser]);

  return {
    user: appUser,
    loading: authLoading || profileLoading,
    profileLoaded,
    ...authMethods,
  };
};